import * as React from "react";
import { createStyles, makeStyles, Paper } from "@material-ui/core";
import {
    domain,
    getUserFromStorage,
    Text,
    RelodeTheme,
    Attachment,
    DisplayField,
} from "../..";
import { AddressLink, AttachmentLinkCollection, Link } from "../link";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            paper: {
                padding: theme.spacing(2),
            },
            noPadding: {
                padding: theme.spacing(0),
                [theme.breakpoints.down("md")]: {
                    padding: theme.spacing(2),
                },
            },
            header: {
                marginBottom: 15,
            },
            companyUrl: {
                marginBottom: 20,
                fontSize: 16,
            },
            companyBackground: {
                padding: theme.spacing(2, 0, 0, 0),
                fontSize: 16,
                whiteSpace: "pre-line",
            },
            addressLink: {
                fontSize: 16,
            },
            companyAttachments: {
                marginTop: 15,
            },
        }),
    { name: "CompanyDetails" }
);

interface CompanyDetailsProps {
    companyUrl?: string;
    companyName: string;
    companyAddress1: string;
    companyAddress2: string;
    companyLocationName: string;
    companyBackground: string;
    actionableLinks: boolean;
    companyAttachments?: Attachment[];
}

export const CompanyDetails = (props: CompanyDetailsProps) => {
    const {
        companyUrl,
        companyName,
        companyAddress1,
        companyAddress2,
        companyBackground,
        companyLocationName,
        actionableLinks,
        companyAttachments,
    } = props;
    const classes = useStyles({});
    const user = getUserFromStorage();
    const isClient =
        user.role === domain.UserRole.Client ||
        user.role === domain.UserRole.ClientAdmin;
    return (
        <Paper elevation={isClient ? 0 : 1} className={isClient ? classes.noPadding : classes.paper}>
            <Text variant="h2" gutterBottom>
                company
            </Text>
            {companyUrl ? (
                <Link
                    id="companyDetails-companyUrl"
                    href={companyUrl}
                    target="_blank"
                    highlightType="external"
                    rel="noopener"
                    className={classes.companyUrl}
                >
                    {companyName}
                </Link>
            ) : (
                <Text
                    className={classes.companyUrl}
                    dataCy="companyDetails-companyUrl-text"
                >
                    {companyName}
                </Text>
            )}

            {companyAddress1 && (
                <Text
                    variant="body1"
                    dataCy="companyDetails-companyAddress1-text"
                >
                    {companyAddress1}
                </Text>
            )}
            {companyAddress2 && (
                <Text
                    variant="body1"
                    dataCy="companyDetails-companyAddress2-text"
                >
                    {companyAddress2}
                </Text>
            )}
            {companyLocationName && (
                <>
                    <Text variant="body1" dataCy="companyDetails-companyLocationName-text" gutterBottom>
                        {companyLocationName}
                    </Text>
                    {actionableLinks && (
                        <AddressLink
                            address1={companyAddress1}
                            address2={companyAddress2}
                            cityStatePostalCode={companyLocationName}
                            className={classes.addressLink}
                        />
                    )}
                </>
            )}
            <Text variant="body1" className={classes.companyBackground} dataCy="companyDetails-companyBackground-text">
                {companyBackground}
            </Text>
            {companyAttachments?.length > 0 ? (
                <div className={classes.companyAttachments}>
                    <DisplayField
                        label="attachments"
                        component={
                            <AttachmentLinkCollection
                                id="company.attachments"
                                attachments={companyAttachments}
                            />
                        }
                    />
                </div>
            ) : null}
        </Paper>
    );
};
